import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { InternalMyProfileService } from '~/modules/admin-shared/services/my-profile.service';
import { AdminAssetsService } from '../../services/admin-assets.service';
import { InternalFilterService } from '../../services/filter.service';
import { Admin } from '../../store/actions/admin.actions';
import { AdminSelectors } from '../../store/selectors/admin.selectors';

@Component({
    selector: 'app-internal-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class InternalFilterComponent implements OnInit, OnChanges {
    public menuItems: { label: string; iconName: string; displayMode?: 'customer' | 'admin' | 'both' }[];
    selectedMenuItem: string;
    vehicleIconName: string;
    statisticsIconName: string;
    customerNotificationsIconName: string;
    isMenuExpanded: boolean = false;
    countries: { name: string, selected: boolean }[] = [];
    shortModeValue: boolean = false;
    latestSelectedCountries: string[] = [];
    isAdminMode: boolean = false;

    @Select(AdminSelectors.getSelectedCountries) selectedCountries$: Observable<string[]>;
    @Input() selectedCountries: string[] = [];
    @Input() isHeaderMode: boolean = false;
    @Output() countrySelectionChange = new EventEmitter<string[]>();
    @Output() removeCountry = new EventEmitter<string>();

    constructor(
        private internalFilterService: InternalFilterService,
        private store: Store,
        private cdr: ChangeDetectorRef,
        public assetService: AdminAssetsService,
        private router: Router,
        private localizeRouterService: LocalizeRouterService,
        private msalService: MsalService,
        private myProfileService: InternalMyProfileService,
    ) {
        this.menuItems = [
            { label: 'Overview', iconName: 'home', displayMode: 'both' },
            // { label: 'Messages', iconName: 'messages', displayMode: 'both' },
            // { label: 'My Notifications', iconName: 'bell', displayMode: 'both' },
            // { label: 'Favorite Vehicles', iconName: 'heart', displayMode: 'both' },
            // { label: 'Financing', iconName: 'money', displayMode: 'customer' },
            { label: 'Compare Vehicles', iconName: 'legal', displayMode: 'both' },
            { label: 'My Profile', iconName: 'user-settings', displayMode: 'both' },
            // { label: 'Bookmark', iconName: 'bookmark', displayMode: 'customer' },
        ];

        this.vehicleIconName = 'trucks-side';
        this.statisticsIconName = 'graph';
        this.customerNotificationsIconName = 'notification-bell-edit';
    }

    ngOnInit() {
        this.getCountries();
        this.selectedCountries$.subscribe((selectedCountries) => {
            this.latestSelectedCountries = selectedCountries;
            this.updateCountrySelection(selectedCountries);
            this.cdr.detectChanges();
        });
        this.isAdminMode = this.myProfileService.isAdmin();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedCountries && changes.selectedCountries.currentValue) {
            this.latestSelectedCountries = changes.selectedCountries.currentValue;
            this.updateCountrySelection(this.latestSelectedCountries);
            this.cdr.detectChanges();
        }
    }

    updateCountrySelection(selectedCountries: string[]): void {
        this.countries = this.countries.map(country => ({
            ...country,
            selected: selectedCountries.includes(country.name)
        }));

        this.cdr.markForCheck();
    }

    private getBasePath(): string {
        if (this.router.url.includes('/administration')) {
            return '/administration';
        } else if (this.router.url.includes('/customer')) {
            return '/customer';
        }
        return '/administration';
    }

    selectMenuItem(item: string): void {
        console.log('Menu item clicked', item);
        this.selectedMenuItem = item;
        this.isMenuExpanded = false;

        const basePath = this.getBasePath();
        let targetPath = '';
        switch (item) {
            case 'Notifications':
                targetPath = '/administration/customer-notifications';
                break;
            case 'Overview':
                targetPath = `${basePath}/overview`;
                break;
            case 'Compare Vehicles':
                targetPath = `${basePath}/compare`;
                break;
            case 'My Profile':
                targetPath = `${basePath}/profile`;
                break;
        }
        if (targetPath) {
            this.router.navigate([this.localizeRouterService.translateRoute(targetPath)]);
        }
    }

    isSelected(item: string): boolean {
        return this.selectedMenuItem === item;
    }

    updateSelectedCountries(): void {
        const selectedCountries = this.countries.filter(country => country.selected).map(country => country.name);
        this.store.dispatch(new Admin.UpdatePagination({
            pagination: {
                currentPage: 1,
                pageSize: 10,
                totalItems: 0,
                totalPages: 0
            }
        }));
        this.store.dispatch(new Admin.UpdateSelectedCountries({ selectedCountries }));
        this.countrySelectionChange.emit(selectedCountries);
    }

    toggleMenu(): void {
        if (!this.router.url.includes('/trucks')) {
            this.router.navigate([this.localizeRouterService.translateRoute('/administration/trucks')]);
            this.isMenuExpanded = true;
        } else {
            this.isMenuExpanded = !this.isMenuExpanded;
        }

        this.selectedMenuItem = 'vehicles';
    }

    getChevronIcon(): SafeHtml {
        return this.isMenuExpanded ? this.chevronUpIcon : this.chevronDownIcon;
    }

    getCountries() {
        this.internalFilterService.getInternalCountryList().subscribe((response) => {
            this.countries = response.countries.map((country) => {
                return { name: country, selected: false };
            });
            this.updateCountrySelection(this.latestSelectedCountries);
            this.cdr.detectChanges();
        });
    }

    trackByCountry(index: number, country: { name: string, selected: boolean }): string {
        return country.name;
    }

    onCheckboxChange(country: { name: string, selected: boolean }, event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        country.selected = checkbox.checked;
        this.updateSelectedCountries();
        if (!this.router.url.includes('/trucks')) {
            this.router.navigate([this.localizeRouterService.translateRoute('/administration/trucks')]);
        }
    }

    shouldShowMenuItem(item: { label: string, displayMode?: 'customer' | 'admin' | 'both' }): boolean {
        if (item.label === 'My Profile') return true;
        if (!item.displayMode || item.displayMode === 'both') return true;
        if (item.displayMode === 'customer') return !this.isAdminMode;
        return this.isAdminMode;
    }

    async logout(): Promise<void> {
        event.preventDefault();
        try {
            await this.myProfileService.logout(this.msalService);
            this.router.navigate(['/']);
        } catch (error) {
            console.error('Logout error:', error);
        }
    }

    getIcon(iconName: string, isSelected: boolean): SafeHtml {
        const suffix = isSelected ? '-dark' : '';
        return this.assetService.getInlineSvg(`${iconName}${suffix}.svg`);
    }

    get vehicleIcon(): SafeHtml {
        return this.getIcon(this.vehicleIconName, this.isMenuExpanded);
    }

    get statisticsIcon(): SafeHtml {
        return this.getIcon(this.statisticsIconName, this.isSelected('statistics'));
    }

    get notificationsIcon(): SafeHtml {
        return this.getIcon(this.customerNotificationsIconName, this.isSelected('Notifications'));
    }

    get logoutIcon(): SafeHtml {
        return this.assetService.getInlineSvg('logout.svg');
    }

    get chevronDownIcon(): SafeHtml {
        return this.assetService.getInlineSvg('chevron-down.svg');
    }

    get chevronUpIcon(): SafeHtml {
        return this.assetService.getInlineSvg('chevron-up.svg');
    }
}
